/**
 * Today screen — implementation for issues #36 and #48.
 *
 * Four sections per PRD (AI Kit Screens → Today):
 *   1. Five priorities (from Executive Agent briefing, or synthesised)
 *   2. Projects needing attention
 *   3. New opportunities (scored deals)
 *   4. Decision Queue (Executive Agent decisions + legacy approvals)
 *
 * #48 additions:
 *   - Executive Agent briefing (A1): populates priorities async; shows
 *     "AI briefing" indicator when live data is available.
 *   - Deals transform fix (A2): accepts d.properties from live API.
 *   - Decision Queue wired to /agents/executive/decisions (A3): Approve/
 *     Defer buttons optimistically remove items from the queue.
 *   - Due follow-ups (A4): fetched from /watchlist/due and folded into
 *     priorities if available.
 *   - Sample-data badge (C2): visible when a section shows fallback data.
 *
 * Design: dark slate + amber (industrial), consistent with global.css tokens.
 * Data: fetched via useAINative().api.call() with graceful degradation.
 */

import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./today/today.css";
import type { Priority, AttentionProject, ScoredDeal, Approval, Decision, DueFollowUp } from "./today/types.js";
import type { SectionState } from "./today/useTodayData.js";
import { useTodayData } from "./today/useTodayData.js";
import {
  FALLBACK_PRIORITIES,
  FALLBACK_ATTENTION_PROJECTS,
  FALLBACK_SCORED_DEALS,
  FALLBACK_APPROVALS,
} from "./today/fallbacks.js";

// ─── Navigation helpers ───────────────────────────────────────────────────────

/**
 * Props that turn a card into a clickable, keyboard-accessible navigation
 * target. Applied to an <article> so the whole card is actionable.
 */
function clickableProps(onActivate: (() => void) | undefined): React.HTMLAttributes<HTMLElement> {
  if (!onActivate) return {};
  return {
    role: "button",
    tabIndex: 0,
    onClick: onActivate,
    onKeyDown: (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onActivate();
      }
    },
    style: { cursor: "pointer" },
  };
}

/**
 * Resolve where a priority item should navigate. Priorities are synthesised from
 * projects / follow-ups / approvals, so route by id prefix and type.
 */
function priorityHref(priority: Priority): string | null {
  if (priority.id.startsWith("prio-project-")) {
    return `/projects/${encodeURIComponent(priority.id.replace("prio-project-", ""))}`;
  }
  if (priority.id.startsWith("prio-followup-")) return "/deals";
  if (priority.id.startsWith("prio-approval-")) return "/today"; // decision queue lives here
  if (priority.type === "Project") return "/projects";
  if (priority.type === "Follow-up" || priority.type === "Opportunity") return "/deals";
  return null;
}

// ─── Small UI primitives ──────────────────────────────────────────────────────

/** Animated skeleton block, used while loading. */
function SkeletonRows({ rows = 3 }: { rows?: number }) {
  const widths: Array<"short" | "medium" | "full"> = ["full", "medium", "short"];
  return (
    <div className="today-skeleton" aria-hidden="true">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className={`today-skeleton__line today-skeleton__line--${widths[i % 3]}`}
        />
      ))}
    </div>
  );
}

/** Generic error banner inside a section. */
function SectionError({ message }: { message: string }) {
  return (
    <div className="today-error" role="alert">
      <span className="today-error__icon" aria-hidden="true">!</span>
      <span className="today-error__text">
        Endpoint unavailable — showing placeholder data.{" "}
        <span style={{ opacity: 0.6 }}>({message})</span>
      </span>
    </div>
  );
}

/** Generic empty-state block inside a section. */
function SectionEmpty({ message, sub }: { message: string; sub?: string }) {
  return (
    <div className="today-empty">
      <div className="today-empty__icon" aria-hidden="true">—</div>
      <p className="today-empty__text">{message}</p>
      {sub && <p className="today-empty__sub">{sub}</p>}
    </div>
  );
}

/**
 * C2: "Sample data" badge — shown when a section is rendering fallback
 * placeholder data rather than live information. Reuses the design pattern
 * from DealsPage's .deals-page__sample-badge.
 */
function SampleDataBadge() {
  return (
    <span
      className="today-sample-badge"
      role="note"
      aria-label="Showing sample data"
      title="This section is showing placeholder data — live data is unavailable"
    >
      Sample data
    </span>
  );
}

/**
 * A1: "AI briefing" indicator — shown when priorities are populated from the
 * live Executive Agent briefing (not fallback data).
 */
function AIBriefingIndicator() {
  return (
    <span
      className="today-ai-badge"
      role="status"
      aria-label="Priorities from AI briefing"
      title="These priorities were generated by the Executive Agent"
    >
      AI briefing
    </span>
  );
}

// ─── Section wrapper ──────────────────────────────────────────────────────────

interface SectionProps {
  title: string;
  badge?: string | number;
  badgeVariant?: "accent" | "neutral";
  /** If true, renders a "Sample data" badge next to the title. */
  isFallback?: boolean;
  /** If true, renders an "AI briefing" indicator next to the title. */
  isAILive?: boolean;
  children: React.ReactNode;
}

function Section({
  title,
  badge,
  badgeVariant = "accent",
  isFallback,
  isAILive,
  children,
}: SectionProps) {
  const badgeClass =
    badgeVariant === "neutral"
      ? "today-section__badge today-section__badge--neutral"
      : "today-section__badge";

  return (
    <section className="today-section" aria-label={title}>
      <div className="today-section__head">
        <h2 className="today-section__title">{title}</h2>
        <div className="today-section__head-right">
          {isFallback && <SampleDataBadge />}
          {isAILive && <AIBriefingIndicator />}
          {badge !== undefined && (
            <span className={badgeClass}>{badge}</span>
          )}
        </div>
      </div>
      <div className="today-section__body">{children}</div>
    </section>
  );
}

// ─── Priority card ────────────────────────────────────────────────────────────

function PriorityCard({ priority, rank }: { priority: Priority; rank: number }) {
  const navigate = useNavigate();
  const href = priorityHref(priority);
  const onActivate = href ? () => navigate(href) : undefined;
  return (
    <article
      className="today-priority"
      aria-label={`Priority ${rank}: ${priority.title}`}
      {...clickableProps(onActivate)}
    >
      <div className="today-priority__rank" aria-label={`Rank ${rank}`}>
        {rank}
      </div>
      <div className="today-priority__body">
        <div className="today-priority__title">{priority.title}</div>
        <div className="today-priority__meta">
          {priority.project && <span>{priority.project}</span>}
          {priority.dueDate && <span>{priority.dueDate}</span>}
          {priority.type && (
            <span
              className={`today-priority__tag today-priority__tag--${priority.level}`}
            >
              {priority.level}
            </span>
          )}
        </div>
      </div>
    </article>
  );
}

// ─── Due follow-up card ───────────────────────────────────────────────────────

function DueFollowUpItem({ item }: { item: DueFollowUp }) {
  return (
    <article className="today-priority" aria-label={`Follow-up: ${item.title}`}>
      <div
        className="today-priority__rank today-priority__rank--followup"
        aria-hidden="true"
      >
        ↑
      </div>
      <div className="today-priority__body">
        <div className="today-priority__title">{item.title}</div>
        <div className="today-priority__meta">
          {item.property && <span>{item.property}</span>}
          {item.contact && <span>{item.contact}</span>}
          {item.dueDate && <span>{item.dueDate}</span>}
        </div>
      </div>
    </article>
  );
}

// ─── Priorities section ───────────────────────────────────────────────────────

/** Priorities section. In order of precedence:
 *  1. Live Executive Agent briefing (A1) — async, replaces fallback when ready
 *  2. Synthesised from projects + approvals data
 *  3. FALLBACK_PRIORITIES (static illustrative data)
 *
 *  Due follow-ups (A4) are folded into the priority list when available.
 */
function PrioritiesSection({
  projects,
  approvals,
  briefingPriorities,
  briefingLive,
  dueFollowUps,
}: {
  projects: SectionState<AttentionProject>;
  approvals: SectionState<Approval>;
  briefingPriorities: SectionState<Priority>;
  briefingLive: boolean;
  dueFollowUps: SectionState<DueFollowUp>;
}) {
  // If executive briefing has live data, use it
  if (briefingLive && briefingPriorities.data.length > 0) {
    const livePriorities = briefingPriorities.data.slice(0, 5);

    return (
      <Section
        title="Today's Priorities"
        badge={livePriorities.length}
        isAILive
      >
        <div className="today-priorities">
          {livePriorities.map((priority, i) => (
            <PriorityCard key={priority.id} priority={priority} rank={i + 1} />
          ))}
        </div>
        {/* Surface due follow-ups below priorities if available */}
        {dueFollowUps.status === "success" && dueFollowUps.data.length > 0 && (
          <div className="today-priorities-followups">
            <div className="today-priorities-followups__label">Due Follow-ups</div>
            {dueFollowUps.data.map((item) => (
              <DueFollowUpItem key={item.id} item={item} />
            ))}
          </div>
        )}
      </Section>
    );
  }

  // Build a dynamic priority list from fetched data where possible
  const dynamicPriorities: Priority[] = [];

  if (approvals.status === "success" && approvals.data.length > 0) {
    // Surface top approval as highest priority
    const topApproval = approvals.data[0];
    if (topApproval !== undefined) {
      const entry: Priority = {
        id: `prio-approval-${topApproval.id}`,
        title: `Review: ${topApproval.title}`,
        project: topApproval.project,
        level: "urgent",
        type: "Approval",
      };
      if (topApproval.requestedAt !== undefined) {
        entry.dueDate = topApproval.requestedAt;
      }
      dynamicPriorities.push(entry);
    }
  }

  // Fold due follow-ups into priorities (A4)
  if (dueFollowUps.status === "success" && dueFollowUps.data.length > 0) {
    dueFollowUps.data.slice(0, 2).forEach((item) => {
      dynamicPriorities.push({
        id: `prio-followup-${item.id}`,
        title: item.title,
        project: item.property,
        level: "high",
        dueDate: item.dueDate,
        type: "Follow-up",
      });
    });
  }

  if (projects.status === "success" && projects.data.length > 0) {
    const redProjects = projects.data.filter((p) => p.status === "red");
    redProjects.slice(0, 2).forEach((p) => {
      dynamicPriorities.push({
        id: `prio-project-${p.id}`,
        title: `Address: ${p.reason}`,
        project: p.name,
        level: "high",
        type: "Project",
      });
    });
  }

  // Pad with fallbacks to reach 5
  const priorities: Priority[] = [
    ...dynamicPriorities,
    ...FALLBACK_PRIORITIES.filter(
      (fp) => !dynamicPriorities.find((dp) => dp.type === fp.type)
    ),
  ].slice(0, 5);

  // If we have no data at all (everything errored), use full fallback
  const displayPriorities =
    priorities.length > 0 ? priorities : FALLBACK_PRIORITIES.slice(0, 5);

  const isFallback = dynamicPriorities.length === 0;

  return (
    <Section
      title="Today's Priorities"
      badge={displayPriorities.length}
      isFallback={isFallback}
    >
      <div className="today-priorities">
        {displayPriorities.map((priority, i) => (
          <PriorityCard key={priority.id} priority={priority} rank={i + 1} />
        ))}
      </div>
    </Section>
  );
}

// ─── Projects needing attention ───────────────────────────────────────────────

function ProjectItem({ project }: { project: AttentionProject }) {
  const navigate = useNavigate();
  const onActivate = () => navigate(`/projects/${encodeURIComponent(project.id)}`);
  return (
    <article
      className="today-project"
      aria-label={project.name}
      {...clickableProps(onActivate)}
    >
      <div
        className={`today-project__dot today-project__dot--${project.status}`}
        aria-label={`Status: ${project.status}`}
      />
      <div className="today-project__body">
        <div className="today-project__name">{project.name}</div>
        <div className="today-project__reason">{project.reason}</div>
      </div>
      {project.phase && (
        <div className="today-project__phase">{project.phase}</div>
      )}
    </article>
  );
}

function ProjectsAttentionSection({ state }: { state: SectionState<AttentionProject> }) {
  const isLoading = state.status === "idle" || state.status === "loading";
  const isError = state.status === "error";
  const isEmpty = state.status === "success" && state.data.length === 0;

  // Data to display: fetched data, fallback on error, or fallback when empty
  const projects =
    state.data.length > 0 ? state.data : FALLBACK_ATTENTION_PROJECTS;

  const badgeCount = isLoading ? "..." : projects.length;

  return (
    <Section
      title="Projects Needing Attention"
      badge={badgeCount}
      badgeVariant={isError ? "neutral" : "accent"}
      isFallback={isError || (state.status === "success" && state.data.length === 0 && FALLBACK_ATTENTION_PROJECTS.length > 0)}
    >
      {isLoading && <SkeletonRows rows={3} />}
      {!isLoading && isError && (
        <>
          <SectionError message={state.error ?? "Unknown error"} />
          {FALLBACK_ATTENTION_PROJECTS.map((p) => (
            <ProjectItem key={p.id} project={p} />
          ))}
        </>
      )}
      {!isLoading && !isError && isEmpty && (
        <SectionEmpty
          message="All projects are on track."
          sub="no items need attention right now"
        />
      )}
      {!isLoading && !isError && !isEmpty && (
        projects.map((p) => <ProjectItem key={p.id} project={p} />)
      )}
    </Section>
  );
}

// ─── New opportunities ────────────────────────────────────────────────────────

function OpportunityItem({ deal }: { deal: ScoredDeal }) {
  const navigate = useNavigate();
  // Deals board is keyed by property entity_id; deep-link to it via the board.
  const onActivate = () => navigate(`/deals?focus=${encodeURIComponent(deal.id)}`);
  return (
    <article
      className="today-opportunity"
      aria-label={deal.address}
      {...clickableProps(onActivate)}
    >
      <div
        className={`today-opportunity__score today-opportunity__score--${deal.scoreTier}`}
        aria-label={`Score: ${deal.score}`}
      >
        {deal.score}
      </div>
      <div className="today-opportunity__body">
        <div className="today-opportunity__address">{deal.address}</div>
        <div className="today-opportunity__meta">
          {deal.askPrice && <span>{deal.askPrice}</span>}
          {deal.capRate && <span>Cap {deal.capRate}</span>}
          {deal.addedDate && <span>{deal.addedDate}</span>}
        </div>
      </div>
    </article>
  );
}

function OpportunitiesSection({ state }: { state: SectionState<ScoredDeal> }) {
  const isLoading = state.status === "idle" || state.status === "loading";
  const isError = state.status === "error";
  const isEmpty = state.status === "success" && state.data.length === 0;

  const deals =
    state.data.length > 0 ? state.data : FALLBACK_SCORED_DEALS;

  const badgeCount = isLoading ? "..." : deals.length;

  const showFallback = isError || (state.status === "success" && state.data.length === 0);

  return (
    <Section
      title="New Opportunities"
      badge={badgeCount}
      badgeVariant={isError ? "neutral" : "accent"}
      isFallback={showFallback}
    >
      {isLoading && <SkeletonRows rows={3} />}
      {!isLoading && isError && (
        <>
          <SectionError message={state.error ?? "Unknown error"} />
          {FALLBACK_SCORED_DEALS.map((d) => (
            <OpportunityItem key={d.id} deal={d} />
          ))}
        </>
      )}
      {!isLoading && !isError && isEmpty && (
        <SectionEmpty
          message="No new opportunities scored yet."
          sub="deals will appear here once the acquisition agent finds matches"
        />
      )}
      {!isLoading && !isError && !isEmpty && (
        deals.map((d) => <OpportunityItem key={d.id} deal={d} />)
      )}
    </Section>
  );
}

// ─── Decision queue (A3) ──────────────────────────────────────────────────────

/**
 * A single decision item from the Executive Agent.
 * Approve/Defer buttons do optimistic removal from the queue.
 * TODO(backend): wire to PATCH /agents/executive/decisions/:id once available.
 */
function DecisionItem({
  decision,
  onApprove,
  onDefer,
}: {
  decision: Decision;
  onApprove: (id: string) => void;
  onDefer: (id: string) => void;
}) {
  const [confirmed, setConfirmed] = useState<"approved" | "deferred" | null>(null);

  const handleApprove = useCallback(() => {
    setConfirmed("approved");
    onApprove(decision.id);
  }, [decision.id, onApprove]);

  const handleDefer = useCallback(() => {
    setConfirmed("deferred");
    onDefer(decision.id);
  }, [decision.id, onDefer]);

  if (confirmed !== null) {
    return (
      <div
        className="today-approval today-approval--confirmed"
        role="status"
        aria-live="polite"
      >
        <span className="today-approval__confirmed-text">
          {confirmed === "approved" ? "Approved" : "Deferred"} — {decision.title}
        </span>
      </div>
    );
  }

  return (
    <article className="today-approval" aria-label={decision.title}>
      <div className="today-approval__type-dot" aria-hidden="true" />
      <div className="today-approval__body">
        <div className="today-approval__title">{decision.title}</div>
        <div className="today-approval__project">
          {decision.project ?? decision.type ?? "Executive Decision"}
          {decision.requestedAt && (
            <span style={{ marginLeft: 8, opacity: 0.7 }}>
              · {decision.requestedAt}
            </span>
          )}
        </div>
        {decision.context && (
          <div className="today-approval__context">{decision.context}</div>
        )}
      </div>
      <div className="today-approval__actions">
        <button
          className="today-approval__btn today-approval__btn--approve"
          aria-label={`Approve: ${decision.title}`}
          onClick={handleApprove}
        >
          Approve
        </button>
        <button
          className="today-approval__btn today-approval__btn--deny"
          aria-label={`Defer: ${decision.title}`}
          onClick={handleDefer}
        >
          Defer
        </button>
      </div>
    </article>
  );
}

/** Legacy approval item (from /approvals endpoint). */
function ApprovalItem({
  approval,
  onApprove,
  onDefer,
}: {
  approval: Approval;
  onApprove: (id: string) => void;
  onDefer: (id: string) => void;
}) {
  const [confirmed, setConfirmed] = useState<"approved" | "deferred" | null>(null);

  const handleApprove = useCallback(() => {
    setConfirmed("approved");
    onApprove(approval.id);
  }, [approval.id, onApprove]);

  const handleDefer = useCallback(() => {
    setConfirmed("deferred");
    onDefer(approval.id);
  }, [approval.id, onDefer]);

  if (confirmed !== null) {
    return (
      <div
        className="today-approval today-approval--confirmed"
        role="status"
        aria-live="polite"
      >
        <span className="today-approval__confirmed-text">
          {confirmed === "approved" ? "Approved" : "Deferred"} — {approval.title}
        </span>
      </div>
    );
  }

  return (
    <article className="today-approval" aria-label={approval.title}>
      <div className="today-approval__type-dot" aria-hidden="true" />
      <div className="today-approval__body">
        <div className="today-approval__title">{approval.title}</div>
        <div className="today-approval__project">
          {approval.project}
          {approval.requestedAt && (
            <span style={{ marginLeft: 8, opacity: 0.7 }}>
              · {approval.requestedAt}
            </span>
          )}
        </div>
      </div>
      <div className="today-approval__actions">
        <button
          className="today-approval__btn today-approval__btn--approve"
          aria-label={`Approve: ${approval.title}`}
          onClick={handleApprove}
        >
          Approve
        </button>
        <button
          className="today-approval__btn today-approval__btn--deny"
          aria-label={`Defer: ${approval.title}`}
          onClick={handleDefer}
        >
          Defer
        </button>
      </div>
    </article>
  );
}

/**
 * Decision Queue section (A3).
 * Prefers executive decisions if available; falls back to /approvals.
 * Items are optimistically removed from the displayed list on Approve/Defer.
 */
function ApprovalsSection({
  decisionsState,
  approvalsState,
}: {
  decisionsState: SectionState<Decision>;
  approvalsState: SectionState<Approval>;
}) {
  // Track which items have been acted on (optimistic removal)
  const [removedIds, setRemovedIds] = useState<Set<string>>(new Set());

  const handleApprove = useCallback((id: string) => {
    // Optimistically remove from displayed list after brief confirmation display
    setTimeout(() => {
      setRemovedIds((prev) => new Set([...prev, id]));
    }, 1200);
    // TODO(backend): PATCH /agents/executive/decisions/:id { action: "approve" }
  }, []);

  const handleDefer = useCallback((id: string) => {
    setTimeout(() => {
      setRemovedIds((prev) => new Set([...prev, id]));
    }, 1200);
    // TODO(backend): PATCH /agents/executive/decisions/:id { action: "defer" }
  }, []);

  // Prefer executive decisions over legacy approvals
  const useExecutiveDecisions =
    decisionsState.status === "success" && decisionsState.data.length > 0;

  const isLoading =
    (decisionsState.status === "idle" || decisionsState.status === "loading") &&
    (approvalsState.status === "idle" || approvalsState.status === "loading");

  if (useExecutiveDecisions) {
    const visibleDecisions = decisionsState.data.filter(
      (d) => !removedIds.has(d.id)
    );

    return (
      <Section
        title="Decision Queue"
        badge={visibleDecisions.length}
        badgeVariant="accent"
        isFallback={false}
      >
        {visibleDecisions.length === 0 ? (
          <SectionEmpty
            message="No pending decisions."
            sub="your inbox is clear"
          />
        ) : (
          visibleDecisions.map((d) => (
            <DecisionItem
              key={d.id}
              decision={d}
              onApprove={handleApprove}
              onDefer={handleDefer}
            />
          ))
        )}
      </Section>
    );
  }

  // Fall back to /approvals endpoint
  const isError = approvalsState.status === "error";
  const isEmpty =
    approvalsState.status === "success" && approvalsState.data.length === 0;

  const approvals =
    approvalsState.data.length > 0 ? approvalsState.data : FALLBACK_APPROVALS;

  const visibleApprovals = approvals.filter((a) => !removedIds.has(a.id));
  const badgeCount = isLoading ? "..." : visibleApprovals.length;

  return (
    <Section
      title="Decision Queue"
      badge={badgeCount}
      badgeVariant={isError ? "neutral" : "accent"}
      isFallback={
        isError ||
        (approvalsState.status === "success" && approvalsState.data.length === 0)
      }
    >
      {isLoading && <SkeletonRows rows={2} />}
      {!isLoading && isError && (
        <>
          <SectionError message={approvalsState.error ?? "Unknown error"} />
          {FALLBACK_APPROVALS.filter((a) => !removedIds.has(a.id)).map((a) => (
            <ApprovalItem
              key={a.id}
              approval={a}
              onApprove={handleApprove}
              onDefer={handleDefer}
            />
          ))}
        </>
      )}
      {!isLoading && !isError && isEmpty && (
        <SectionEmpty
          message="No pending decisions."
          sub="your inbox is clear"
        />
      )}
      {!isLoading && !isError && !isEmpty && (
        visibleApprovals.map((a) => (
          <ApprovalItem
            key={a.id}
            approval={a}
            onApprove={handleApprove}
            onDefer={handleDefer}
          />
        ))
      )}
    </Section>
  );
}

// ─── Today page ───────────────────────────────────────────────────────────────

export function TodayPage() {
  const {
    projects,
    deals,
    approvals,
    decisions,
    dueFollowUps,
    briefingPriorities,
    briefingLive,
    userName,
  } = useTodayData();

  // Format today's date
  const today = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const greeting = userName
    ? `Good morning, ${userName.split(" ")[0]}.`
    : "Good morning.";

  return (
    <main className="today-page" aria-label="Today dashboard">
      <header className="today-page__header">
        {/* Screen title — required by AppShell integration test */}
        <h1 className="today-page__greeting">Today</h1>
        <p className="today-page__greeting today-page__greeting--sub">{greeting}</p>
        <p className="today-page__date">{today}</p>
      </header>

      <div className="today-grid">
        {/* Left column — priorities + projects */}
        <div className="today-grid__main">
          <PrioritiesSection
            projects={projects}
            approvals={approvals}
            briefingPriorities={briefingPriorities}
            briefingLive={briefingLive}
            dueFollowUps={dueFollowUps}
          />
          <ProjectsAttentionSection state={projects} />
        </div>

        {/* Right column — decision queue above opportunities */}
        <div className="today-grid__sidebar">
          <ApprovalsSection
            decisionsState={decisions}
            approvalsState={approvals}
          />
          <OpportunitiesSection state={deals} />
        </div>
      </div>
    </main>
  );
}
